import { _Node, nodeTypes, connectorTypes, nodeDetails, PseudoConnector, _Connector, NodeDump } from "./Node";
import { Connection, ConnectionDump } from "./Connection";
import chroma from "chroma-js";
import { Point } from "./Point";
import { getAbsolutePos, getMousePos } from "./mousePos";
import { Plots } from "babyplots";

const helpTexts = [
    ["Node Plot Creator", "With the Node Plot Creator, you can interactively build babyplots visualizations, without writing a single line of code. You can download the plots that you create here as a .json file, to display them e.g. in the PowerPoint add-in, or you can publish them to your babyplots account, to easily share them with others."],
    [
        "Usage",
        "The Node Plot Creator works by adding and connecting nodes. Different nodes have different functions: There are e.g. input nodes that allow you to load in data, plot nodes that generate the visualizations, and much more.",
        "You can find the nodes by clicking the orange \"+\" button in the top left corner. You can add a node by dragging and dropping it from this list into the grey area. You can move nodes around in the grey area by clicking and holding the colored header of the node and moving the mouse. To delete a node from the grey area, drag and drop it over the \"trash bin\" button in the top center, which appears as soon as you start dragging a node.",
        "Once you have added a node, you will notice that the node has options (e.g. input elements, upload buttons, or drop-down menus), these control certain properties of the node.",
        "You will also find that the node has colored circles at the borders of the node. These are connectors and allow you to connect nodes to other nodes. Connectors at the left border of a node are inputs, connectors at the right border are outputs.",
        "Clicking on an output connector while holding the control key on your keyboard shows a preview of what the node outputs at this connector.",
        "You can only connect outputs to inputs and vice versa. Furthermore, only connectors of the same color can be connected to each other. The colors correspond to the type of data that the input expects, or the output provides.",
        "You can click the colored header of a node while pressing the control key to minimize/restore the node. Minimized nodes only show the connectors, the options are hidden. This is useful to keep the overview when your node network becomes large.",
        "Changes you make to a node are updated in real time and propagated through all connected nodes.",
        "You can save and download your Creator project using the download button (the left most button in the top right button bar). You can load a saved project using the upload button (the button in the middle of the top right button bar)."
    ],
    ["More information", "This is just a short overview of the functionality of the Node Plot Creator. For a detailed description, head to the documentation."]
]

export interface ProjectDump {
    connections: ConnectionDump[];
    nodes: NodeDump[];
    screenshot: string;
}

export class Creator {
    private _dragging: boolean = false;
    private _dragTarget: _Node;
    private _draggingConnection: boolean = false;
    private _mouseConnector: PseudoConnector;
    private _draggedConnector: _Connector;
    private _tempConnection: Connection;
    private _newNodeClone: HTMLDivElement;
    private _newNodeDraggingOffset: Point;
    private _createContainer: HTMLDivElement;
    private _helpContainer: HTMLDivElement;
    private _remButton: HTMLDivElement;
    private _popupPreview: HTMLDivElement;
    private _popupLoad: HTMLDivElement;
    private _loadError: HTMLParagraphElement;
    private _loggedIn: boolean = false;
    private _loadedProject: ProjectDump;
    private _loadedProjectPreviewImg: HTMLImageElement;
    private _p: Plots;
    private _publishMethod: (plotData: {}, plot: Plots) => void;

    canvas: HTMLCanvasElement;
    ctx: CanvasRenderingContext2D;
    nodes: _Node[] = [];
    connections: Connection[] = [];
    _currentPlotData: {};

    constructor(canvas: HTMLCanvasElement, loggedIn: boolean, publishMethod?: (plotData: {}, plot: Plots) => void) {
        this.canvas = canvas;
        this._loggedIn = loggedIn;
        let boundingRect = this.canvas.getBoundingClientRect();
        this.canvas.width = boundingRect.width;
        this.canvas.height = boundingRect.height;
        this.canvas.style.backgroundColor = "#eee";
        this.ctx = canvas.getContext("2d");
        this.canvas.addEventListener("pointermove", this.mouseMove.bind(this));
        this.canvas.addEventListener("pointerdown", this._mouseDown.bind(this));
        this.canvas.addEventListener("pointerup", this._mouseUp.bind(this));
        this.update();
        this._createUI();
        this._publishMethod = publishMethod;
    }

    private _createUI() {
        // Load project from file button
        let loadButton = document.createElement("div");
        loadButton.className = "npc load-button";
        let loadButtonPos = getAbsolutePos(this.canvas, { x: this.canvas.width, y: 0 });
        loadButton.style.right = (document.body.clientWidth - loadButtonPos.x) + "px";
        loadButton.style.top = loadButtonPos.y + "px";
        loadButton.title = "Load a creator project from a file."
        let load = document.createElement("i");
        load.className = "fas fa-file-upload";
        loadButton.append(load);
        this.canvas.parentElement.append(loadButton);
        loadButton.addEventListener("click", (function () {
            let creator = this as Creator;
            let openProjectBtn = document.getElementById("openProjectBtn");
            openProjectBtn.style.opacity = "0";
            creator._loadError.style.display = "none";
            creator._loadedProjectPreviewImg.style.display = "none";
            creator._popupLoad.style.display = "block";
        }).bind(this));
        // Load project pop-up
        let popupLoadOverlay = document.createElement("div");
        this.canvas.parentElement.append(popupLoadOverlay);
        this._popupLoad = popupLoadOverlay;
        popupLoadOverlay.className = "preview-overlay";
        let loadBox = document.createElement("div");
        loadBox.className = "load-box";
        popupLoadOverlay.append(loadBox);
        let loadHeading = document.createElement("h2");
        loadHeading.innerText = "Load a saved Creator project from a file.";
        loadBox.append(loadHeading);
        let loadWarning = document.createElement("p");
        loadWarning.innerText = "Warning: Loading a project clears the current Creator project.";
        loadWarning.className = "npc load-warning";
        loadBox.append(loadWarning);
        let loadInput = document.createElement("input");
        loadInput.type = "file";
        loadInput.className = "custom-file-input";
        loadInput.onchange = this.importProject.bind(this);
        loadBox.append(loadInput);
        let loadError = document.createElement("p");
        loadError.className = "npc load-error";
        this._loadError = loadError;
        let loadPreviewImg = document.createElement("img");
        loadPreviewImg.className = "npc load-preview";
        loadPreviewImg.style.display = "none";
        this._loadedProjectPreviewImg = loadPreviewImg;
        loadBox.append(loadPreviewImg);
        loadBox.append(loadError);
        let loadBtnRow = document.createElement("div");
        loadBtnRow.className = "btn-row";
        let closeLoadBtn = document.createElement("div");
        closeLoadBtn.addEventListener("click", (function () {
            let creator = this as Creator;
            creator._popupLoad.style.display = "none";
        }).bind(this));
        closeLoadBtn.innerText = "Close";
        closeLoadBtn.className = "close-button";
        closeLoadBtn.id = "closeLoadBtn";
        let closeLoadIcon = document.createElement("i");
        closeLoadIcon.className = "fas fa-times";
        closeLoadBtn.prepend(closeLoadIcon);
        loadBtnRow.append(closeLoadBtn);
        let openProjectBtn = document.createElement("div");
        openProjectBtn.addEventListener("click", this.load.bind(this));
        openProjectBtn.innerText = "Load";
        openProjectBtn.className = "npc open-project-button";
        openProjectBtn.id = "openProjectBtn";
        openProjectBtn.style.opacity = "0";
        let openIcon = document.createElement("i");
        openIcon.className = "fas fa-check";
        openProjectBtn.prepend(openIcon);
        loadBtnRow.append(openProjectBtn);
        loadBox.append(loadBtnRow);
        // Save project button
        let saveButton = document.createElement("div");
        saveButton.className = "npc save-button";
        saveButton.title = "Download this creator project."
        let saveButtonPos = getAbsolutePos(this.canvas, { x: this.canvas.width, y: 0 });
        saveButton.style.right = (document.body.clientWidth - saveButtonPos.x) + "px";
        saveButton.style.top = saveButtonPos.y + "px";
        let dl = document.createElement("i");
        dl.className = "fas fa-file-download";
        saveButton.append(dl);
        this.canvas.parentElement.append(saveButton);
        saveButton.addEventListener("click", (function () {
            let creator = this as Creator;
            creator.exportProject();
        }).bind(this));
        // Help button
        let helpButton = document.createElement("div");
        helpButton.className = "npc help-button";
        let helpButtonPos = getAbsolutePos(this.canvas, { x: this.canvas.width, y: 0 });
        helpButton.style.right = (document.body.clientWidth - helpButtonPos.x) + "px";
        helpButton.style.top = helpButtonPos.y + "px";
        let info = document.createElement("i");
        info.className = "fas fa-info";
        helpButton.append(info);
        this.canvas.parentElement.append(helpButton);
        // Help page
        let helpContainer = document.createElement("div");
        this._helpContainer = helpContainer;
        helpButton.addEventListener("click", (function () {
            let creator = this as Creator;
            creator._helpContainer.style.display = "block";
        }).bind(this));
        helpContainer.className = "npc help";
        helpContainer.style.right = (document.body.clientWidth - helpButtonPos.x) + "px";
        helpContainer.style.top = helpButtonPos.y + "px";
        this.canvas.parentElement.append(helpContainer);
        let helpCloseButton = document.createElement("div");
        helpCloseButton.style.textAlign = "right";
        let helpCloseX = document.createElement("i");
        helpCloseX.className = "fa fa-times";
        helpCloseX.addEventListener("click", (function () {
            let creator = this as Creator;
            creator._helpContainer.style.display = "none";
        }).bind(this));
        helpCloseButton.append(helpCloseX);
        helpContainer.append(helpCloseButton);
        for (let i = 0; i < helpTexts.length; i++) {
            const section = helpTexts[i];
            for (let j = 0; j < section.length; j++) {
                const text = section[j];
                let textElem: HTMLElement;
                if (j === 0) {
                    textElem = document.createElement("h1");
                    textElem.className = "npc help-heading";
                } else {
                    textElem = document.createElement("p");
                    textElem.className = "npc help-text";
                }
                textElem.innerText = text;
                helpContainer.append(textElem);
            }
        }
        // Add new nodes button
        let addButton = document.createElement("div");
        addButton.className = "npc add-button";
        let buttonPos = getAbsolutePos(this.canvas, { x: 0, y: 0 });
        addButton.style.left = buttonPos.x + "px";
        addButton.style.top = buttonPos.y + "px";
        let plus = document.createElement("i");
        plus.className = "fa fa-plus";
        addButton.append(plus);
        this.canvas.parentElement.append(addButton);
        // remove nodes button
        let remButton = document.createElement("div");
        remButton.className = "npc rem-button";
        let trashPos = getAbsolutePos(this.canvas, {
            x: this.canvas.width / 2 - 30,
            y: 0
        });
        remButton.style.left = trashPos.x + "px";
        remButton.style.top = trashPos.y + "px";
        let trash = document.createElement("i");
        trash.className = "far fa-trash-alt";
        remButton.addEventListener("pointermove", this.mouseMove.bind(this));
        remButton.addEventListener("pointerup", this._mouseUp.bind(this));
        remButton.append(trash);
        this.canvas.parentElement.append(remButton);
        this._remButton = remButton;
        // Node pop up list
        let createListContainer = document.createElement("div");
        this._createContainer = createListContainer;
        addButton.addEventListener("click", (function () {
            let creator = this as Creator;
            creator._createContainer.style.display = "block";
        }).bind(this));
        createListContainer.className = "npc node-create";
        createListContainer.style.left = buttonPos.x + "px";
        createListContainer.style.top = buttonPos.y + "px";
        let createCloseButton = document.createElement("div");
        let createCloseX = document.createElement("i");
        createCloseX.className = "fa fa-times";
        createCloseX.addEventListener("click", (function () {
            let creator = this as Creator;
            creator._createContainer.style.display = "none";
        }).bind(this));
        createCloseButton.append(createCloseX);
        createListContainer.append(createCloseButton);
        let createList = document.createElement("div");
        createList.className = "node-list";
        createListContainer.append(createList);
        // populate node pop up list with node types
        let createdSections = {};
        for (const nodeName in nodeTypes) {
            if (nodeTypes.hasOwnProperty(nodeName)) {
                const nodeDtls = nodeDetails[nodeName];
                let sectionName = nodeDtls[0][0];
                let nodeSection: HTMLDivElement;
                if (createdSections.hasOwnProperty(sectionName)) {
                    // find existing node section
                    nodeSection = createdSections[sectionName];
                } else {
                    // create new section and append it to the node list
                    nodeSection = document.createElement("div");
                    nodeSection.className = "node-section-box";
                    nodeSection.id = "section_" + sectionName;
                    let nodeSectionHeader = document.createElement("div");
                    nodeSectionHeader.className = "node-section-header";
                    let nodeSectionHeading = document.createElement("h3");
                    nodeSectionHeading.innerText = sectionName + " Nodes:"
                    nodeSectionHeader.append(nodeSectionHeading);
                    nodeSection.append(nodeSectionHeader);
                    createList.append(nodeSection);
                    createdSections[sectionName] = nodeSection;
                }
                let createNodeBox = document.createElement("div");
                createNodeBox.className = "node-box";
                createNodeBox.style.borderColor = nodeDtls[1];
                let createNodeHeader = document.createElement("div");
                createNodeHeader.className = "new-node-header";
                createNodeHeader.style.backgroundColor = nodeDtls[1];
                let createNodeTitle = document.createElement("h4");
                createNodeTitle.innerText = nodeName;
                if (chroma(nodeDtls[1]).luminance() < 0.5) {
                    createNodeTitle.style.color = "white";
                }
                else {
                    createNodeTitle.style.color = "black";
                }
                createNodeHeader.append(createNodeTitle);
                let createNodeBody = document.createElement("div");
                createNodeBody.className = "body";
                for (let i = 1; i < nodeDtls[0].length; i++) {
                    const descText = nodeDtls[0][i];
                    let createNodeDesc = document.createElement("p");
                    createNodeDesc.innerText = descText;
                    createNodeBody.append(createNodeDesc);
                }
                let createNodeRef = document.createElement("p");
                let createNodeRefLink = document.createElement("a");
                createNodeRefLink.href = "https://bp.bleb.li/documentation/creator#" + nodeName;
                createNodeRefLink.target = "_blank";
                createNodeRefLink.innerText = "Reference";
                createNodeRef.append(createNodeRefLink);
                createNodeBody.append(createNodeRef);
                createNodeBox.append(createNodeHeader);
                createNodeBox.append(createNodeBody);
                createNodeBox.setAttribute('data-nodetype', nodeName);
                createNodeHeader.addEventListener("mouseenter", function () { createNodeBody.className = "body hover"; createNodeHeader.className = "new-node-header hover"; });
                createNodeHeader.addEventListener("mouseleave", function () { createNodeBody.className = "body"; createNodeHeader.className = "new-node-header"; });
                createNodeHeader.addEventListener("pointerdown", this._newNodeDrag.bind(this));
                nodeSection.append(createNodeBox);
            }
        }
        this.canvas.parentElement.append(createListContainer);
        this._createContainer = createListContainer;

        // create pop up viewer
        let popupPreviewOverlay = document.createElement("div");
        popupPreviewOverlay.className = "preview-overlay";
        let previewBox = document.createElement("div");
        previewBox.className = "preview-box";
        // plot name input
        let plotnameLabel = document.createElement("label");
        plotnameLabel.innerText = "Plot name:";
        plotnameLabel.htmlFor = "plotName";
        let plotnameInput = document.createElement("input");
        plotnameInput.value = "untitled plot";
        plotnameInput.id = "plotName";
        plotnameInput.name = "plotName";
        plotnameInput.type = "text";
        // canvas title
        let previewHeading = document.createElement("h5");
        previewHeading.innerText = "Preview"
        let previewHeadingIcon = document.createElement("i");
        previewHeading.prepend(previewHeadingIcon);
        // preview canvas
        let canvasBox = document.createElement("div");
        canvasBox.className = "canvas-box";
        let previewCanvas = document.createElement("canvas");
        previewCanvas.id = "creatorPreviewCanvas";
        previewCanvas.className = "preview-canvas";
        canvasBox.append(previewCanvas);
        // button row
        let btnRow = document.createElement("div");
        btnRow.className = "btn-row";
        // close button
        let closePreviewBtn = document.createElement("div");
        closePreviewBtn.addEventListener("click", this.hidePreview.bind(this));
        closePreviewBtn.innerText = "Close";
        closePreviewBtn.className = "close-button";
        closePreviewBtn.id = "closePreviewBtn";
        let closeIcon = document.createElement("i");
        closeIcon.className = "fas fa-times";
        closePreviewBtn.prepend(closeIcon);
        btnRow.append(closePreviewBtn);
        // export as json button
        let exportPreviewBtn = document.createElement("div");
        exportPreviewBtn.addEventListener("click", this.exportPreview.bind(this));
        exportPreviewBtn.innerText = "Download";
        exportPreviewBtn.className = "export-button";
        exportPreviewBtn.id = "exportPreviewBtn";
        let exportIcon = document.createElement("i");
        exportIcon.className = "fas fa-file-download";
        exportPreviewBtn.prepend(exportIcon);
        btnRow.append(exportPreviewBtn);

        previewBox.append(
            plotnameLabel,
            plotnameInput
        )

        if (this._loggedIn) {
            let descriptionLabel = document.createElement("label");
            descriptionLabel.innerText = "Description:";
            descriptionLabel.htmlFor = "plotDescription";
            let descriptionInput = document.createElement("textarea");
            descriptionInput.id = "plotDescription";
            descriptionInput.name = "plotDescription";
            let listedLabel = document.createElement("label");
            listedLabel.innerText = "Listed:";
            listedLabel.htmlFor = "plotListed";
            let listedInput = document.createElement("input");
            listedInput.type = "checkbox";
            listedInput.name = "plotListed";
            listedInput.id = "plotListed";
            listedInput.checked = true;
            let privateLabel = document.createElement("label");
            privateLabel.innerText = "Private:";
            privateLabel.htmlFor = "plotPrivate";
            let privateInput = document.createElement("input");
            privateInput.type = "checkbox";
            privateInput.name = "plotPrivate";
            privateInput.id = "plotPrivate";
            privateInput.checked = false;
            let publishPreviewBtn = document.createElement("div");
            publishPreviewBtn.addEventListener("click", this.publishPreview.bind(this));
            publishPreviewBtn.innerText = "Publish";
            publishPreviewBtn.className = "publish-button";
            publishPreviewBtn.id = "publishPreviewBtn";
            let publishIcon = document.createElement("i");
            publishIcon.className = "fas fa-file-import";
            publishPreviewBtn.prepend(publishIcon);
            btnRow.append(publishPreviewBtn);
            previewBox.append(
                descriptionLabel,
                descriptionInput,
                listedLabel,
                listedInput,
                privateLabel,
                privateInput
            );
        }
        previewBox.append(
            previewHeading,
            canvasBox,
            btnRow
        );

        popupPreviewOverlay.append(previewBox);
        this.canvas.parentElement.append(popupPreviewOverlay);
        this._popupPreview = popupPreviewOverlay;
    }

    private _newNodeDrag(evt: MouseEvent) {
        evt.stopPropagation();
        if (this._newNodeClone) {
            return
        }
        let nNdiv = (evt.currentTarget as HTMLDivElement).parentNode as HTMLDivElement;
        this._newNodeClone = nNdiv.cloneNode(true) as HTMLDivElement;
        this._newNodeClone.removeEventListener("pointerdown", this._newNodeDrag.bind(this));
        this._newNodeClone.className += " dragged";
        this._newNodeClone.style.width = nNdiv.clientWidth + "px";
        this.canvas.parentElement.append(this._newNodeClone);
        let mousePos = { x: evt.clientX, y: evt.clientY };
        this._newNodeDraggingOffset = {
            x: nNdiv.getBoundingClientRect().left - mousePos.x,
            y: nNdiv.getBoundingClientRect().top - mousePos.y
        };
        this._newNodeClone.style.position = "absolute";
        this._newNodeClone.style.left = mousePos.x + this._newNodeDraggingOffset.x + "px";
        this._newNodeClone.style.top = mousePos.y + this._newNodeDraggingOffset.y + "px";
        this._newNodeClone.addEventListener("pointermove", this.mouseMove.bind(this));
        this._newNodeClone.addEventListener("pointerup", this._mouseUp.bind(this));
    }

    mouseMove(evt: MouseEvent) {
        let mousePos = getMousePos(this.canvas, evt);
        if (this._dragging) {
            this._dragTarget.move(mousePos);
            this._remButton.style.display = "block";
            this.update();
        }

        if (this._draggingConnection) {
            this._mouseConnector.updatePosition(mousePos.x, mousePos.y);
            this.update();
        }

        if (this._newNodeClone) {
            let mousePos = { x: evt.clientX, y: evt.clientY };
            this._newNodeClone.style.left = mousePos.x + this._newNodeDraggingOffset.x + "px";
            this._newNodeClone.style.top = mousePos.y + this._newNodeDraggingOffset.y + "px";
        }
    }

    private _mouseDown(evt: MouseEvent) {
        let mousePos = getMousePos(this.canvas, evt);
        for (let i = 0; i < this.nodes.length; i++) {
            const node = this.nodes[i];
            // check if mouse is over node header for moving a node
            if (node.isInHeader(mousePos)) {
                if (evt.ctrlKey) {
                    node.toggleMinimize();
                }
                this._dragging = true;
                this._dragTarget = node;
                node.dragged = true;
                node.dragOffset = {
                    x: node.xPos - mousePos.x,
                    y: node.yPos - mousePos.y
                }
                break;
            }
            // check if mouse is in node body
            let inNode = node.isInNode(mousePos);
            // if mouse is in a connector for creating/removing connections
            // or if ctrl is pressed, show output preview, if available
            if (inNode && inNode.conn) {
                if (!inNode.conn.isInput && evt.ctrlKey) {
                    this.update();
                    inNode.conn.showPreview();
                    break;
                }
                if (!this._draggingConnection) {
                    this._draggingConnection = true;
                    this._mouseConnector = new PseudoConnector(mousePos, !inNode.conn.isInput);
                    if (inNode.conn.isConnected && inNode.conn.isInput) {
                        // find and remove previously connected connection
                        for (let j = 0; j < this.connections.length; j++) {
                            const connection = this.connections[j];
                            if (connection.a === inNode.conn || connection.b === inNode.conn) {
                                this.connections.splice(j, 1);
                                connection.dissolve();
                                this._mouseConnector.isInput = true;
                                if (connection.a.isInput) {
                                    this._draggedConnector = connection.b;
                                } else {
                                    this._draggedConnector = connection.a;
                                }
                                break;
                            }
                        }
                    } else {
                        this._draggedConnector = inNode.conn;
                    }
                    this.addConnection(this._draggedConnector, this._mouseConnector, true);
                }
                break;
            }
        }
    }

    private _mouseUp(evt: MouseEvent) {
        if (this._dragging) {
            // test if mouse is over trash icon.
            let boundingRect = this._remButton.getBoundingClientRect();
            if (
                evt.clientX >= boundingRect.left &&
                evt.clientX <= boundingRect.right &&
                evt.clientY >= boundingRect.top &&
                evt.clientY <= boundingRect.bottom
            ) {
                // remove node and all its connections
                for (let i = this.connections.length - 1; i >= 0; i--) {
                    const conn = this.connections[i];
                    if (conn.a.parentNode === this._dragTarget || conn.b.parentNode === this._dragTarget) {
                        this.connections.splice(i, 1);
                        conn.dissolve();
                    }
                }
                this._dragTarget.dispose();
                let ndIdx = this.nodes.indexOf(this._dragTarget);
                if (ndIdx !== -1) {
                    this.nodes.splice(ndIdx, 1);
                }
            }
            this._dragging = false;
            this._dragTarget.dragged = false;
            this._dragTarget = undefined;
            this._remButton.style.display = "none";
            this.update();
        }
        if (this._draggingConnection) {
            // check if mouse is on a connector with correct type
            let mousePos = getMousePos(this.canvas, evt);
            let noConn = true;
            for (let i = 0; i < this.nodes.length; i++) {
                const node = this.nodes[i];
                let t = node.isInNode(mousePos);
                if (t && t.conn) {
                    if (t.conn.isInput === this._mouseConnector.isInput && t.conn.type === this._draggedConnector.type) {
                        if (t.conn.isConnected && t.conn.isInput) {
                            // find and remove previously connected connection
                            for (let j = 0; j < this.connections.length; j++) {
                                const connection = this.connections[j];
                                if (connection.a === t.conn || connection.b === t.conn) {
                                    this.connections.splice(j, 1);
                                    connection.dissolve();
                                    break;
                                }
                            }
                        }
                        this._tempConnection.dissolve();
                        this.addConnection(this._draggedConnector, t.conn);
                        this._draggingConnection = false;
                        this._mouseConnector = undefined;
                        this._tempConnection = undefined;
                        this._draggedConnector = undefined;
                        noConn = false;
                        this.update();
                    }
                    break;
                }
            }
            // if mouse after dragging is not over a valid connector, remove the connection.
            if (noConn) {
                if (this._draggedConnector.isInput && this._draggedConnector.autoCreateNodeType !== undefined) {
                    let nNode = this.addNode(this._draggedConnector.autoCreateNodeType, mousePos.x, mousePos.y);
                    if (nNode !== undefined) {
                        this.addConnection(this._draggedConnector, nNode.outputs[this._draggedConnector.autoCreateNodeConnector]);
                    }
                } else {
                    // this._draggedConnector.isConnected = false;
                    this._tempConnection.dissolve();
                }
                this._draggingConnection = false;
                this._mouseConnector = undefined;
                this._tempConnection = undefined;
                this._draggedConnector = undefined;
                this.update();
            }
        }
        if (this._newNodeClone) {
            let mousePos = getMousePos(this.canvas, evt);
            // determine if mouse is over node creation list pop up
            let boundingRect = this._createContainer.getBoundingClientRect();
            if (
                !(
                    mousePos.x >= boundingRect.left &&
                    mousePos.x <= boundingRect.right &&
                    mousePos.y >= boundingRect.top &&
                    mousePos.y <= boundingRect.bottom
                )
            ) {
                // only add new node if mouse has dragged clone out of node creation list pop up
                let newNodeType = this._newNodeClone.dataset.nodetype;
                this.addNode(
                    newNodeType,
                    mousePos.x + this._newNodeDraggingOffset.x,
                    mousePos.y + this._newNodeDraggingOffset.y
                );
            }
            // destroy new node div and all its child elements
            this._newNodeClone.querySelectorAll('*').forEach(n => n.remove());
            this._newNodeClone.remove();
            this._newNodeClone = undefined;
        }
    }

    update(background = false) {
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        if (background) {
            this.ctx.fillStyle = "#eeeeee";
            this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
        }
        for (let i = this.connections.length - 1; i >= 0; i--) {
            const conn = this.connections[i];
            if (!conn.a.enabled || !conn.b.enabled || conn.a.destroyed || conn.b.destroyed) {
                conn.dissolve();
                this.connections.splice(i, 1);
                continue
            }
            conn.update();
            conn.draw();
        }
        for (let i = 0; i < this.nodes.length; i++) {
            const node = this.nodes[i];
            if (!node.dragged) {
                node.draw();
            }
        }
        if (this._dragging) {
            this._dragTarget.draw();
        }
        if (this._draggingConnection) {
            this._tempConnection?.update();
            this._tempConnection?.draw();
        }
    }

    /**
     * Add a node of specified type to the creator at given coordinates.
     * @param nodeType 
     * @param x 
     * @param y 
     */
    public addNode(nodeType: string, x: number, y: number): _Node {
        if (!nodeTypes.hasOwnProperty(nodeType)) {
            return;
        }
        let newNode: _Node;
        if (["pointCloud", "shapeCloud", "surface", "heatMap", "babyplots"].indexOf(nodeType) !== -1) {
            newNode = nodeTypes[nodeType](x, y, this.canvas, (function () { (this as Creator).update() }).bind(this), (function (plotData: {}) { (this as Creator).showPreview(plotData) }).bind(this));
        } else {
            newNode = nodeTypes[nodeType](x, y, this.canvas, (function () { (this as Creator).update() }).bind(this));
        }
        // pass through mouse move event from node option divs to creator canvas
        for (let i = 0; i < newNode.options.length; i++) {
            const option = newNode.options[i];

            option.div.addEventListener(
                "pointermove",
                this.mouseMove.bind(this)
            );
        }
        this.nodes.push(newNode);
        this.update();
        return newNode;
    }

    /**
     * addConnection
     */
    public addConnection(from: _Connector, to: _Connector, temp: boolean = false, calculate: boolean = true) {
        let newConnection = new Connection(from, to, this.ctx);
        if (temp) {
            this._tempConnection = newConnection;
            from.connectTo(to);
        } else {
            this.connections.push(newConnection);
            from.connectTo(to);
            if (calculate) {
                if (from.isInput) {
                    from.parentNode.calculateValues();
                } else {
                    to.parentNode.calculateValues();
                }
            }
        }
        this.update();
    }

    resize() {
        let trashPos = getAbsolutePos(this.canvas, {
            x: this.canvas.width / 2 - 30,
            y: 0
        });
        this._remButton.style.left = trashPos.x + "px";
        this._remButton.style.top = trashPos.y + "px";
        let boundingRect = this.canvas.getBoundingClientRect();
        this.canvas.width = boundingRect.width;
        this.canvas.height = boundingRect.height;
        this.update();
    }

    showPreview(plotData: {}) {
        this._currentPlotData = plotData;
        this._popupPreview.style.display = "block";
        this._p = new Plots("creatorPreviewCanvas");
        this._p.fromJSON(plotData);
        this._p.createButtons(["label"]);
        this._p.doRender();
        this._p.resize();
    }

    hidePreview() {
        if (this._p !== undefined) {

            this._p.dispose();
            this._p = undefined;
        }
        this._popupPreview.style.display = "none";
        this._currentPlotData = undefined;
    }

    publishPreview() {
        this._publishMethod(this._currentPlotData, this._p);
        this.hidePreview();
    }

    exportPreview() {
        let dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(this._currentPlotData));
        let dlElem = document.createElement("a");
        dlElem.setAttribute("href", dataStr);
        let plotName = (document.getElementById("plotName") as HTMLInputElement).value;
        dlElem.setAttribute("download", plotName + ".json");
        dlElem.click();
        dlElem.remove();
    }

    save(): ProjectDump {
        let connections = this.connections.map((c) => c.save());
        let nodes = this.nodes.map((n) => n.save());
        this.update(true);
        let screenshot = this.canvas.toDataURL("image/jpeg", 0.1);
        this.update(false);
        let project = { connections: connections, nodes: nodes, screenshot: screenshot }
        return project;
    }

    exportProject() {
        let dump = this.save();
        let dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(dump));
        let dlElem = document.createElement("a");
        dlElem.setAttribute("href", dataStr);
        dlElem.setAttribute("download", "npcProject.json");
        dlElem.click();
        dlElem.remove();
    }

    load() {
        if (this._loadedProject === undefined) {
            this._loadError.innerText = "Error: Loading failed.";
            this._loadError.style.display = "block";
            return;
        }
        // clear current creator project
        for (let nds = 0; nds < this.nodes.length; nds++) {
            const nd = this.nodes[nds];
            nd.dispose();
        }
        this.nodes = [];
        for (let cns = 0; cns < this.connections.length; cns++) {
            const cn = this.connections[cns];
            cn.dissolve();
        }
        this.connections = [];
        // create nodes
        let nodes: {[id: string]: _Node} = {} // keep nodes in an object for association with connectors
        for (let i = 0; i < this._loadedProject.nodes.length; i++) {
            const nodeData = this._loadedProject.nodes[i];
            let node = this.addNode(nodeData.type, nodeData.xPos, nodeData.yPos);
            // transfer node details
            node.id = nodeData.id;
            node.name = nodeData.name;
            node.values = nodeData.values;
            node.minimized = false;
            nodes[node.id] = node;
            // setup options
            for (let o = 0; o < node.options.length; o++) {
                const opt = node.options[o];
                opt.changeValue(nodeData.options[o].input);
                if (opt.onChange !== undefined) {
                    opt.onChange();
                }
                if (opt.connectable && nodeData.options[o].connector !== undefined) {
                    opt.connector.id = nodeData.options[o].connector;
                }
                opt.enabled = nodeData.options[o].enabled;
            }
            // setup inputs
            for (let inp = 0; inp < nodeData.inputs.length; inp++) {
                const inputData = nodeData.inputs[inp];
                let nodeInput = node.inputs[inp];
                if (nodeInput === undefined) {
                    let newConnector = connectorTypes[inputData.type](node, inputData.name, inputData.isInput) as _Connector;
                    node.inputs.push(newConnector);
                    nodeInput = newConnector;
                }
                nodeInput.id = inputData.id;
                nodeInput.enabled = inputData.enabled;
            }
            // setup outputs
            for (let out = 0; out < nodeData.outputs.length; out++) {
                const outputData = nodeData.outputs[out];
                let nodeOutput = node.outputs[out];
                if (nodeOutput === undefined) {
                    let newConnector = connectorTypes[outputData.type](node, outputData.name, outputData.isInput) as _Connector;
                    node.outputs.push(newConnector);
                    nodeOutput = newConnector;
                }
                nodeOutput.id = outputData.id;
                nodeOutput.enabled = outputData.enabled;
            }
            node.calcHeight();
        }
        // create connections
        for (let c = 0; c < this._loadedProject.connections.length; c++) {
            const connData = this._loadedProject.connections[c];
            // find connection "from" connector
            let aId = connData.a.id;
            let aParent = nodes[connData.a.parentNode];
            if (aParent === undefined) {
                // do nothing if parent node cannot be found.
                continue;
            }
            let a: _Connector;
            let searchA: _Connector[];
            // search in either inputs or outputs
            if (connData.a.isInput) {
                let optionConnectors = [];
                for (let o = 0; o < aParent.options.length; o++) {
                    if (aParent.options[o].connectable) {
                        optionConnectors.push(aParent.options[o].connector);
                    }
                }
                searchA = aParent.inputs.concat(optionConnectors);
            } else {
                searchA = aParent.outputs;
            }
            for (let i = 0; i < searchA.length; i++) {
                const candidateConn = searchA[i]
                if (candidateConn.id === aId) {
                    a = candidateConn;
                    break;
                }
            }
            let bId = connData.b.id;
            let bParent = nodes[connData.b.parentNode];
            if (bParent === undefined) {
                // do nothing if parent node cannot be found.
                continue;
            }
            let b: _Connector;
            let searchB: _Connector[];
            // search in either inputs or outputs
            if (connData.b.isInput) {
                let optionConnectors = [];
                for (let o = 0; o < bParent.options.length; o++) {
                    if (bParent.options[o].connectable) {
                        optionConnectors.push(bParent.options[o].connector);
                    }
                }
                searchB = bParent.inputs.concat(optionConnectors);
            } else {
                searchB = bParent.outputs;
            }
            for (let i = 0; i < searchB.length; i++) {
                const candidateConn = searchB[i]
                if (candidateConn.id === bId) {
                    b = candidateConn;
                    break;
                }
            }
            if (a === undefined || b === undefined) {
                // for now, do nothing if the connectors cannot be found.
                continue;
            }
            this.addConnection(a, b, false);
            let conn = this.connections[this.connections.length - 1];
            conn.id = connData.id;
        }
        // after all connections have been set, minimize nodes that are minimized in saved project.
        for (let idx = 0; idx < this.nodes.length; idx++) {
            const node = this.nodes[idx];
            const nodeData = this._loadedProject.nodes[idx];
            if (nodeData.minimized) {
                node.toggleMinimize();
            }
        }
        this.update();
        this._popupLoad.style.display = "none";
        this._loadedProject = undefined;
    }

    importProject(evt: Event) {
        let files = (<HTMLInputElement>evt.target).files;
        let reader = new FileReader();
        let openProjectBtn = document.getElementById("openProjectBtn");
        openProjectBtn.style.opacity = "0";
        this._loadError.style.display = "none";
        this._loadedProjectPreviewImg.style.display = "none";
        reader.onload = (function (this: Creator, event) {
            let d = event.target.result as string;
            let projectDump: ProjectDump;
            try {
                projectDump = JSON.parse(d);
            } catch (error) {
                this._loadError.innerText = "Error: Invalid project file provided.";
                this._loadError.style.display = "block";
                return
            }
            if (!projectDump["connections"] || !projectDump["nodes"] || !projectDump["screenshot"]) {
                this._loadError.innerText = "Error: Invalid project file provided.";
                this._loadError.style.display = "block";
                return
            }
            this._loadedProject = projectDump;
            openProjectBtn.style.opacity = "1";
            this._loadedProjectPreviewImg.src = projectDump.screenshot;
            this._loadedProjectPreviewImg.onload = (function (this: Creator, _) {
                this._loadedProjectPreviewImg.style.display = "block";
            }).bind(this);

        }).bind(this);
        reader.readAsText(files[0]);
    }
}
